import { MessageCircle, Trash2 } from 'lucide-react'
import { useRef } from 'react'
import { Column, Row } from 'src/shared/components/Semantic/all'
import { Button } from 'src/shared/components/ui/button'
import { cn } from 'src/shared/components/ui/utils'
import { useLocalization } from 'src/shared/hooks/useLocalization'
import { DRAG_THRESHOLD } from '../consts'
import type { CanvasComment, CardPosition } from '../types'

type DragState = {
  pointerId: number
  startClientX: number
  startClientY: number
  originX: number
  originY: number
  moved: boolean
}

export const CanvasCommentPin = ({
  comment,
  scale,
  isOpen,
  onToggleOpen,
  onMove,
  onDelete,
  onContextMenu,
}: {
  comment: CanvasComment
  scale: number
  isOpen: boolean
  onToggleOpen: () => void
  onMove?: (position: CardPosition) => void
  onDelete?: () => void
  onContextMenu?: (e: React.MouseEvent) => void
}) => {
  const { t } = useLocalization()
  const dragState = useRef<DragState | null>(null)

  const handlePointerDown = (e: React.PointerEvent<HTMLDivElement>) => {
    if (e.button !== 0) return
    e.stopPropagation()
    if ((e.target as HTMLElement).closest('button, textarea')) return
    dragState.current = {
      pointerId: e.pointerId,
      startClientX: e.clientX,
      startClientY: e.clientY,
      originX: comment.x,
      originY: comment.y,
      moved: false,
    }
    try {
      e.currentTarget.setPointerCapture(e.pointerId)
    } catch {
      // Pointer may already be released; drag still works via bubbling events.
    }
  }

  const handlePointerMove = (e: React.PointerEvent<HTMLDivElement>) => {
    const drag = dragState.current
    if (!drag || drag.pointerId !== e.pointerId) return
    const dxClient = e.clientX - drag.startClientX
    const dyClient = e.clientY - drag.startClientY
    if (!drag.moved && Math.hypot(dxClient, dyClient) > DRAG_THRESHOLD) {
      drag.moved = true
    }
    if (drag.moved && onMove) {
      onMove({
        x: drag.originX + dxClient / scale,
        y: drag.originY + dyClient / scale,
      })
    }
  }

  const handlePointerUp = (e: React.PointerEvent<HTMLDivElement>) => {
    const drag = dragState.current
    if (!drag || drag.pointerId !== e.pointerId) return
    dragState.current = null
    if (!drag.moved) onToggleOpen()
  }

  const time = new Date(comment.createdAt).toLocaleTimeString([], {
    hour: '2-digit',
    minute: '2-digit',
  })

  return (
    <div
      className="pointer-events-auto absolute left-0 top-0"
      style={{
        transform: `translate(${comment.x}px, ${comment.y}px)`,
        zIndex: isOpen ? 20 : 10,
      }}
      onPointerDown={handlePointerDown}
      onPointerMove={handlePointerMove}
      onPointerUp={handlePointerUp}
      onPointerCancel={() => {
        dragState.current = null
      }}
      onContextMenu={onContextMenu}
    >
      {/* Counter-scale so pins keep a constant screen size, anchored at the tip. */}
      <div style={{ transform: `scale(${1 / scale})`, transformOrigin: '0 0' }}>
        <div className="-translate-y-full">
          <div
            className={cn(
              'flex h-8 w-8 cursor-pointer items-center justify-center rounded-full rounded-bl-none border-2 border-white bg-blue-600 text-white shadow-lg transition-transform',
              'hover:scale-110',
              isOpen && 'scale-110'
            )}
          >
            <MessageCircle className="h-4 w-4" />
          </div>

          {isOpen && (
            <Column
              className="absolute bottom-0 left-10 w-64 cursor-auto gap-2 rounded-xl border border-gray-200 bg-white p-3 shadow-xl"
              onPointerDown={(e) => e.stopPropagation()}
            >
              <Row className="items-center justify-between">
                <Row className="items-center gap-2">
                  <span className="flex h-6 w-6 items-center justify-center rounded-full bg-blue-600 text-[10px] font-medium text-white">
                    {t('You', 'Du').charAt(0).toUpperCase()}
                  </span>
                  <span className="text-xs font-medium text-gray-900">
                    {t('You', 'Du')}
                  </span>
                  <span className="text-[11px] text-gray-400">{time}</span>
                </Row>
                {onDelete ? (
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-6 w-6"
                    onClick={onDelete}
                    aria-label={t('Delete comment', 'Ta bort kommentar')}
                  >
                    <Trash2 className="h-3.5 w-3.5 text-gray-400" />
                  </Button>
                ) : null}
              </Row>
              <p className="whitespace-pre-wrap text-xs text-gray-700">
                {comment.text}
              </p>
            </Column>
          )}
        </div>
      </div>
    </div>
  )
}
